import type {
  SearchReproductionEntity,
  SearchReproductionFilter,
  SearchReproductionRow,
} from './ReproduceSearchService';
import { json_headers, request } from './common';
import type { RequestOptions, SearchServiceEnvelope } from './common';

type RawAnalyticsEntityLocation = {
  latitude: number;
  longitude: number;
};

type RawAnalyticsEntity = {
  id: string;
  name: string;
  location: RawAnalyticsEntityLocation | null;
};

type RawAnalyticsFilter = {
  type: string | null;
  label: string | null;
  value: string | null;
};

type RawAnalyticsTopSearch = {
  rank: number;
  search_id: string | null;
  natural_language_query: string | null;
  query_count: number;
  brand: string | null;
  region: string | null;
  platform: string | null;
  language: string | null;
  search_type: string | null;
  destinations: Array<RawAnalyticsEntity>;
  landmarks: Array<RawAnalyticsEntity>;
  properties: Array<RawAnalyticsEntity>;
  start_date: string | null;
  end_date: string | null;
  adults: number | null;
  children: number | null;
  sort_value: string | null;
  luxplus_tier: string | null;
  loyalty_tier: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  filters: Array<RawAnalyticsFilter>;
};

type RawAnalyticsSearchCategoryResult = {
  search_category: string;
  search_count: number;
  top_searches: Array<RawAnalyticsTopSearch>;
};

type RawAnalyticsSearchStatsResponse = {
  dateRange: {
    startDate: string;
    endDate: string;
  };
  categories: Array<RawAnalyticsSearchCategoryResult>;
};

export type AnalyticsSearchStatsParams = {
  startDate: string;
  endDate: string;
  limit: number;
};

export type AnalyticsTopSearch = Omit<SearchReproductionRow, 'naturalLanguageQuery'> & {
  rank: number;
  searchId: string;
  naturalLanguageQuery: string;
  queryCount: number;
};

export type AnalyticsSearchCategoryResult = {
  searchCategory: string;
  searchCount: number;
  topSearches: Array<AnalyticsTopSearch>;
};

export type AnalyticsDateRange = {
  startDate: string;
  endDate: string;
};

type AnalyticsSearchStatsResponse = {
  dateRange: AnalyticsDateRange;
  categories: Array<AnalyticsSearchCategoryResult>;
};

function searchAdminSearchesStatsPath(): string {
  return `${window.configs.API_HOST}/api/search/admin/v1/searches/stats`;
}

function normaliseAnalyticsEntity(entity: RawAnalyticsEntity): SearchReproductionEntity {
  return {
    id: entity.id,
    name: entity.name,
    location:
      entity.location == null
        ? null
        : {
            latitude: entity.location.latitude,
            longitude: entity.location.longitude,
          },
  };
}

function normaliseAnalyticsFilters(filters: Array<RawAnalyticsFilter>): Array<SearchReproductionFilter> {
  return filters.map((filter) => ({
    type: filter.type,
    label: filter.label,
    value: filter.value,
  }));
}

function normaliseAnalyticsSearchCategoryResults(
  categories: Array<RawAnalyticsSearchCategoryResult>,
): Array<AnalyticsSearchCategoryResult> {
  return categories.map((result) => ({
    searchCategory: result.search_category,
    searchCount: result.search_count,
    topSearches: result.top_searches
      .filter(
        (search): search is RawAnalyticsTopSearch & { search_id: string; natural_language_query: string } =>
          search.search_id != null && search.natural_language_query != null,
      )
      .map((search) => ({
        brand: search.brand,
        region: search.region,
        platform: search.platform,
        language: search.language,
        searchType: search.search_type,
        destinations: (search.destinations ?? []).map(normaliseAnalyticsEntity),
        landmarks: (search.landmarks ?? []).map(normaliseAnalyticsEntity),
        properties: (search.properties ?? []).map(normaliseAnalyticsEntity),
        startDate: search.start_date,
        endDate: search.end_date,
        adults: search.adults,
        children: search.children,
        sortValue: search.sort_value,
        luxplusTier: search.luxplus_tier,
        loyaltyTier: search.loyalty_tier,
        utmSource: search.utm_source,
        utmMedium: search.utm_medium,
        utmCampaign: search.utm_campaign,
        filters: normaliseAnalyticsFilters(search.filters ?? []),
        rank: search.rank,
        searchId: search.search_id,
        naturalLanguageQuery: search.natural_language_query,
        queryCount: search.query_count,
      })),
  }));
}

export function normaliseAnalyticsSearchStatsResponse(
  response: RawAnalyticsSearchStatsResponse,
): AnalyticsSearchStatsResponse {
  return {
    dateRange: {
      startDate: response.dateRange.startDate,
      endDate: response.dateRange.endDate,
    },
    categories: normaliseAnalyticsSearchCategoryResults(response.categories),
  };
}

export async function getAnalyticsSearchStats(
  params: AnalyticsSearchStatsParams,
  options: RequestOptions = {},
): Promise<AnalyticsSearchStatsResponse> {
  const queryString = new URLSearchParams({
    startDate: params.startDate,
    endDate: params.endDate,
    limit: String(params.limit),
  }).toString();

  const response = (await request(`${searchAdminSearchesStatsPath()}?${queryString}`, {
    method: 'GET',
    headers: json_headers,
    signal: options.signal,
  })) as SearchServiceEnvelope<RawAnalyticsSearchStatsResponse>;

  return normaliseAnalyticsSearchStatsResponse(response.result);
}
